import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Snackbar, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import UserFetch, { UserPost } from '../../userFetch';
import useOwnerNames from '../../hooks/useOwnerNames';

export interface SubscriptionFormData {
  owner: string;
  status: string;
  expiry_date: string | null;
  grace_days: number;
  notes: string | null;
}

interface TenantToken {
  id: number;
  token: string;
  owner: string;
  label: string | null;
  created: string;
}

interface CreateSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SubscriptionFormData) => void;
  editData?: SubscriptionFormData | null;
}

function CreateSubscriptionDialog({ open, onClose, onSave, editData }: CreateSubscriptionDialogProps) {
  const { ownerNames } = useOwnerNames();
  const [owner, setOwner] = React.useState('');
  const [status, setStatus] = React.useState('active');
  const [expiryDate, setExpiryDate] = React.useState('');
  const [graceDays, setGraceDays] = React.useState(14);
  const [notes, setNotes] = React.useState('');

  const [tokens, setTokens] = React.useState<TenantToken[]>([]);
  const [snackbar, setSnackbar] = React.useState('');

  React.useEffect(() => {
    if (editData) {
      setOwner(editData.owner);
      setStatus(editData.status);
      setExpiryDate(editData.expiry_date ? editData.expiry_date.split('T')[0] : '');
      setGraceDays(editData.grace_days);
      setNotes(editData.notes || '');
      fetchTokens(editData.owner);
    } else {
      resetForm();
    }
  }, [editData, open]);

  const fetchTokens = (ownerName: string) => {
    UserFetch('/api/tenant-tokens')
      .then((response) => response.json())
      .then((data: TenantToken[]) => setTokens(data.filter(t => t.owner === ownerName)));
  };

  const handleGenerateToken = () => {
    UserPost('/api/tenant-tokens', { owner })
      .then((response) => {
        if (response.ok) fetchTokens(owner);
      });
  };

  const handleDeleteToken = (id: number) => {
    UserFetch(`/api/tenant-tokens/${id}`, { method: 'DELETE' })
      .then((response) => {
        if (response.ok) fetchTokens(owner);
      });
  };

  const copyToken = (token: string) => {
    navigator.clipboard.writeText(token).then(() => {
      setSnackbar('Token copied to clipboard');
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (owner) {
      onSave({
        owner,
        status,
        expiry_date: expiryDate || null,
        grace_days: graceDays,
        notes: notes || null
      });
      resetForm();
    }
  };

  const resetForm = () => {
    setOwner('');
    setStatus('active');
    setExpiryDate('');
    setGraceDays(14);
    setNotes('');
    setTokens([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{editData ? 'Edit Subscription' : 'Create Subscription'}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={ownerNames}
            value={owner || null}
            onChange={(_e, value) => setOwner(value || '')}
            disabled={!!editData}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus={!editData}
                required
                margin="dense"
                label="Owner"
                size="small"
              />
            )}
          />
          <TextField
            select
            margin="dense"
            label="Status"
            fullWidth
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="grace">Grace</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Expiry Date"
            type="date"
            fullWidth
            size="small"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Leave blank for no expiry"
          />
          <TextField
            margin="dense"
            label="Grace Days"
            type="number"
            fullWidth
            size="small"
            value={graceDays}
            onChange={(e) => setGraceDays(parseInt(e.target.value) || 0)}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            size="small"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {editData && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">API Token</Typography>
                {tokens.length === 0 && (
                  <Button size="small" startIcon={<AddIcon />} onClick={handleGenerateToken}>
                    Generate
                  </Button>
                )}
              </Box>
              {tokens.map((t) => (
                <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', flex: 1, wordBreak: 'break-all' }}>
                    {t.token}
                  </Typography>
                  <Tooltip title="Copy token">
                    <IconButton size="small" onClick={() => copyToken(t.token)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Revoke token">
                    <IconButton size="small" onClick={() => handleDeleteToken(t.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              {tokens.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No API token. Devices need a token to connect.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">{editData ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </form>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2000}
        onClose={() => setSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbar('')}>{snackbar}</Alert>
      </Snackbar>
    </Dialog>
  );
}

export default CreateSubscriptionDialog;
